/* eslint-disable no-console */

import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback: ReactNode;
}

interface State {
  hasError: boolean;
}

const ErrorBoundaryContext = React.createContext<{
  triggerError: (value: Error) => void;
}>({
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  triggerError: () => {},
});

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
    };

    this.triggerError = this.triggerError.bind(this);
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error({ error, errorInfo });
  }

  public static getDerivedStateFromError(): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  public triggerError(error: Error): void {
    console.error(error);
    this.setState({ hasError: true });
  }

  public render() {
    const { hasError } = this.state;
    const { children, fallback } = this.props;
    return (
      <ErrorBoundaryContext.Provider
        // eslint-disable-next-line react/jsx-no-constructed-context-values
        value={{ triggerError: this.triggerError }}
      >
        {hasError ? fallback : children}
      </ErrorBoundaryContext.Provider>
    );
  }
}

export const useError = () => {
  return React.useContext(ErrorBoundaryContext);
};
